/**
 * Страница профиля сотрудника
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWorkerStore } from '../store/workerStore'
import { apiClient } from '../services/apiClient'
import { BottomNav } from '../components/BottomNav'
import { PushNotificationService } from '../services/pushNotificationService'
import './ProfilePage.css'

interface Employee {
  id: string
  full_name: string
  phone_number: string | null
  role: string
  is_active: boolean
  organization_id: string
  sites: Array<{
    id: string
    name: string
  }>
}

// Маппинг ролей на русский
const ROLE_LABELS: Record<string, string> = {
  'employee': 'Сотрудник',
  'manager': 'Менеджер',
  'admin': 'Администратор',
  'security': 'Охранник',
  'developer': 'Разработчик'
}

type NavTab = 'qr' | 'sites' | 'schedule' | 'profile'

export function ProfilePage() {
  const navigate = useNavigate()
  const [employee, setEmployee] = useState<Employee | null>(null)
  const [loading, setLoading] = useState(true)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [activeTab] = useState<NavTab>('profile')
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default')
  const [isPushLoading, setIsPushLoading] = useState(false)
  
  const { employeeId, reset: clearOnboarding } = useWorkerStore()
  
  const handleTabChange = (tab: NavTab) => {
    if (tab === 'qr') {
      navigate('/')
    } else if (tab === 'sites') {
      navigate('/sites')
    } else if (tab === 'schedule') {
      navigate('/schedule')
    }
    // profile - уже на этой странице
  }

  useEffect(() => {
    loadEmployeeData()
    
    // Проверяем текущее разрешение на push
    if (PushNotificationService.isSupported()) {
      setPushPermission(Notification.permission)
    }
  }, [employeeId])

  const loadEmployeeData = async () => {
    if (!employeeId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const data = await apiClient.get<Employee>(`/api/v1/worker/employee/${employeeId}`)
      setEmployee(data)
    } catch (error) {
      console.error('[ProfilePage] Failed to load employee data:', error)
      
      // Проверяем, является ли это ошибкой удаленного сотрудника
      const { EmployeeDeletedError } = await import('../errors/EmployeeDeletedError')
      if (error instanceof EmployeeDeletedError) {
        // Перенаправляем на экран удаленного сотрудника
        navigate('/employee-deleted')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setShowResetConfirm(true)
  }

  const confirmReset = () => {
    clearOnboarding()
    // Перезагрузка страницы чтобы вернуться на онбординг
    window.location.href = '/'
  }

  const cancelReset = () => {
    setShowResetConfirm(false)
  }

  const handleEnablePush = async () => {
    if (!employeeId) return
    
    setIsPushLoading(true)
    try {
      await PushNotificationService.subscribe(employeeId, true)
      setPushPermission(Notification.permission)
    } catch (error) {
      console.error('[ProfilePage] Failed to enable push:', error)
      alert('Не удалось включить уведомления. Попробуйте еще раз.')
    } finally {
      setIsPushLoading(false)
    }
  }

  const handleDisablePush = async () => {
    setIsPushLoading(true)
    try {
      await PushNotificationService.unsubscribe()
      setPushPermission(Notification.permission)
      alert('Уведомления отключены')
    } catch (error) {
      console.error('[ProfilePage] Failed to disable push:', error)
      alert('Не удалось отключить уведомления')
    } finally {
      setIsPushLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="spinner"></div>
          <p>Загрузка профиля...</p>
        </div>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="profile-page">
        <div className="profile-error">
          <p>⚠️ Не удалось загрузить данные профиля</p>
          <button onClick={loadEmployeeData} className="btn-retry">
            Повторить
          </button>
        </div>
      </div>
    )
  }

  const roleLabel = ROLE_LABELS[employee.role] || employee.role

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-avatar">
          {employee.full_name.charAt(0).toUpperCase()}
        </div>
        <h1 className="profile-name">{employee.full_name}</h1>
        <p className="profile-role">{roleLabel}</p>
      </div>

      <div className="profile-info">
        <div className="info-section">
          <h2>📱 Контакты</h2>
          <div className="info-item">
            <span className="info-label">Телефон:</span>
            <span className="info-value">{employee.phone_number || 'Не указан'}</span>
          </div>
        </div>

        <div className="info-section">
          <h2>🏢 Доступные объекты</h2>
          {employee.sites && employee.sites.length > 0 ? (
            <div className="sites-list">
              {employee.sites.map(site => (
                <div key={site.id} className="site-item">
                  📍 {site.name}
                </div>
              ))}
            </div>
          ) : (
            <p className="info-empty">Нет доступных объектов</p>
          )}
        </div>

        {/* Секция уведомлений */}
        {PushNotificationService.isSupported() && (
          <div className="info-section">
            <h2>🔔 Уведомления</h2>
            <p className="push-description">
              Получайте важные сообщения от администратора: изменения графика, новости и объявления
            </p>
            
            {pushPermission === 'granted' ? (
              <button 
                onClick={handleDisablePush} 
                className="btn-push btn-push-disable"
                disabled={isPushLoading}
              >
                {isPushLoading ? 'Отключение...' : '🔕 Отключить уведомления'}
              </button>
            ) : (
              <>
                <button 
                  onClick={handleEnablePush} 
                  className="btn-push btn-push-enable"
                  disabled={isPushLoading}
                >
                  {isPushLoading ? 'Включение...' : '🔔 Включить уведомления'}
                </button>
                {pushPermission === 'denied' && (
                  <p className="push-hint">
                    ⚠️ Разрешения заблокированы в настройках браузера.<br />
                    Откройте <strong>Настройки → Safari → app.recvi.ru</strong> и разрешите уведомления.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div className="info-section">
          <h2>⚙️ Настройки</h2>
          <div className="info-item">
            <span className="info-label">ID сотрудника:</span>
            <span className="info-value info-id">{employee.id}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Статус:</span>
            <span className={`info-badge ${employee.is_active ? 'active' : 'inactive'}`}>
              {employee.is_active ? '✅ Активен' : '⛔ Неактивен'}
            </span>
          </div>
        </div>
      </div>

      <div className="profile-actions">
        <button onClick={handleReset} className="btn-reset">
          🔄 Сбросить приложение
        </button>
      </div>

      {showResetConfirm && (
        <div className="modal-overlay" onClick={cancelReset}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>⚠️ Подтверждение</h2>
            <p>
              Вы уверены, что хотите сбросить приложение?
              <br />
              <br />
              Это действие удалит все локальные данные и вам потребуется заново пройти онбординг.
            </p>
            <div className="modal-actions">
              <button onClick={cancelReset} className="btn-cancel">
                Отмена
              </button>
              <button onClick={confirmReset} className="btn-confirm-reset">
                Да, сбросить
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Нижняя навигация */}
      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  )
}
